// Utility to derive the Prism service base URL based on provided rules.
// Rules:
// 1. If on DEV env and not localhost use VITE_API_MGR_BASE_URL
// 2. If localhost and the function code exists use VITE_PRISM_SERVICE_URL
//    - VITE_PRISM_SERVICE_URL may be the https://ari-prism-prod-svc-tst.azurewebsites.net/api site
// Fallback: return first available of API manager base or prism service URL or empty string.
/**
 * Determines whether the app is running in a non-production environment by comparing `VITE_APP_SERVER` against the string `'prod'`.
 */
export const isDevEnv = import.meta.env.VITE_APP_SERVER !== 'prod';

/**
 * Derives the Prism service base URL from environment variables, following precedence rules for localhost, development, and production configurations.
 *
 * If `VITE_USE_LOCALHOST_API` is truthy, returns `VITE_LOCALHOST_API_URL` immediately. Otherwise, for localhost or dev with a function code present, returns `VITE_PRISM_SERVICE_URL`. Falls back to `VITE_API_MGR_BASE_URL` + `VITE_API_FUNCTIONAPP`.
 */
export function getPrismBaseUrl(): string {
  const apiMgrBase = import.meta.env.VITE_API_MGR_BASE_URL as string | undefined;
  const prismServiceUrl = import.meta.env.VITE_PRISM_SERVICE_URL as string | undefined;
  const functionCode = import.meta.env.VITE_PRISM_SERVICE_FUNC_CODE as string | undefined;
  const functionApp = import.meta.env.VITE_API_FUNCTIONAPP as string | undefined;
  const useLocalhostApi = (import.meta.env.VITE_USE_LOCALHOST_API==="true") as boolean | undefined;
  const localhostApi = import.meta.env.VITE_LOCALHOST_API_URL as string | undefined;

  if(useLocalhostApi && localhostApi) {
    return localhostApi
  }

  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const isLocalhost = host === 'localhost' || host === '127.0.0.1';

  // Rule 2: localhost & function code exists
  if ((isLocalhost || isDevEnv) && functionCode) {
    return prismServiceUrl || '';
  }

  return `${apiMgrBase}/${functionApp}`;
}

/**
 * The Azure Function code string used for local development API calls; `undefined` in production builds.
 */
export const prismFunctionCode =
  isDevEnv && import.meta.env.VITE_PRISM_SERVICE_FUNC_CODE
    ? import.meta.env.VITE_PRISM_SERVICE_FUNC_CODE
    : undefined;

export default getPrismBaseUrl;
